/**
 * useBlueprintTableData Hook
 *
 * Drop-in replacement for `useTableData` when a Table Explorer tab is in
 * Blueprint-edit mode (`tabContent.blueprintEdit` is set). Instead of querying
 * a live database, it reads the blueprint's `.data.json` sidecar and maps it
 * into the SAME result shape (`{ rows, columns, totalRowCount }`) the grid
 * consumes. Filtering / sorting / paging are applied CLIENT-side over the small
 * in-memory rowset (see `buildBlueprintTableData`).
 *
 * It mirrors the `useTableData` return surface (`data`, `isLoading`,
 * `isFetching`, `error`, `refetch`, `populationProgress`,
 * `currentExecutionIdRef`, `activeExecutionId`) so `TableExplorerTab` can branch
 * between the two hooks with minimal divergence.
 */

import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { readSidecar } from '../../../database-blueprint/services/databaseBlueprintApi';
import { buildBlueprintTableData } from '../utils/blueprintSidecarRows';
import type { FilterGroup, SortDefinition, TableExplorerColumn } from '../../core/types/workspaceTypes';

type UseBlueprintTableDataParams = {
  folderPath: string;
  schema: string;
  tableName: string;
  limit: number;
  offset?: number;
  filters: FilterGroup[];
  sorting: SortDefinition[];
  enabled: boolean;
  /**
   * Bumped by the tab after a successful save so the query refetches the
   * freshly-written sidecar. Part of the query key.
   */
  reloadToken?: number;
};

type BlueprintTableDataResult = {
  rows: Record<string, unknown>[];
  columns: TableExplorerColumn[];
  totalRowCount?: number;
  // Present (always undefined) so this result type stays structurally
  // compatible with `useTableData`'s `TableDataResult` where the two hooks are
  // merged via a ternary in `TableExplorerTab`. Blueprint mode never spills to
  // a windowed cache and has no LOB preview truncation (CLOB/NCLOB/BLOB/BFILE), so these are never set.
  lobColumns?: string[];
  cacheUri?: string;
  populationMode?: 'in-memory' | 'windowed';
};

export function useBlueprintTableData(params: UseBlueprintTableDataParams) {
  const {
    folderPath,
    schema,
    tableName,
    limit,
    offset = 0,
    filters,
    sorting,
    enabled,
    reloadToken = 0,
  } = params;

  // Present only to mirror useTableData's return surface; blueprint mode never
  // runs a cancellable server fetch, so it stays null (as does the
  // `activeExecutionId` companion below).
  const currentExecutionIdRef = useRef<string | null>(null);

  const query = useQuery<BlueprintTableDataResult>({
    queryKey: [
      'blueprint-table-data',
      folderPath,
      schema,
      tableName,
      limit,
      offset,
      filters,
      sorting,
      reloadToken,
    ],
    queryFn: async () => {
      const r = await readSidecar({ folderPath, schema, name: tableName });
      if (!r.success) {
        throw new Error(r.error || 'Failed to read blueprint sidecar');
      }
      if (!r.sidecar) {
        // Sidecar missing on disk — render an empty grid rather than erroring.
        return { rows: [], columns: [], totalRowCount: 0 };
      }
      const built = buildBlueprintTableData({
        sidecar: r.sidecar,
        filters,
        sorting,
        limit,
        offset,
      });
      return {
        rows: built.rows,
        columns: built.columns,
        totalRowCount: built.totalRowCount,
      };
    },
    enabled: enabled && !!folderPath && !!tableName,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    // No streaming in blueprint mode — rows are inlined synchronously.
    populationProgress: undefined,
    isPopulating: false,
    currentExecutionIdRef,
    activeExecutionId: null as string | null,
  };
}
